// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";
import Validator from "@classes/Validator";

// COMPONENTS
import Checkbox from "@components/Checkbox/Checkbox";
import DefaultFields from "../../../components/DefaultFields/DefaultFields";
import EditableField from "../../../components/EditableField/EditableField";
import UboFields from "../../../components/UboFields/UboFields";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";

// HELPERS
import {createClassName, getPropertiesByPrefix, parseDraftDataPropsToFieldState, delay} from "@helpers/utils";
import * as CobHelpers from "../../../Cob.helpers";
import fulfillmentCheck from "./CustomerInformation.fulfillment";
import Button from "../../../../../components/Button/Button";
import StackLayout from "../../../../../components/layouts/StackLayout/StackLayout";
import OptionsList from "../../../components/OptionsList/OptionsList";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CheckboxWithName from "../../../components/CheckboxWithName/CheckboxWithName";
import Select from "../../../../../components/Select/Select";
import countries from "@helpers/countries";

const countriesOptions = countries.map(({label, code}) => ({label, value: code}));

export default class CobCustomerInformation extends React.PureComponent {
	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired,
	};

	static companyExclusive = false;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState)
	{
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields)
		{
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}

		if (prevProps.draftData !== props.draftData)
		{
			if (props.draftData)
			{
				// get the draftData props specific to this section only
				const draftDataProps = getPropertiesByPrefix(props.draftData, ["customer_", "contact_", "checkbox_", "radio_", "kyc_", "company_", "ubo_"]);
				// parse the draftData props into state.fields
				const newFieldsState = parseDraftDataPropsToFieldState(this.state.fields, draftDataProps);
				this.setState(
					{
						fields: {...newFieldsState}
					},
					async () =>
					{
						await delay(200);
						onChange(id, {...this.state}, fulfillmentCheck(this.props, this.state));
					}
				);
			}
		}
	}

	render() {
		const {props, state} = this;
		const {title, session, partner} = props;
		const classes = createClassName("Cob-CustomerInformation", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled,
		});

		let ssnLong = state.fields.customer_social_security_number.replaceAll("-", "");
		let ssnShort = ssnLong;
		if (ssnLong.length > 10)
		{
			ssnShort = ssnShort.substring(2, ssnShort.length);
		}

		let selectedCitizenshipIndex = -1;
		for (let i = 0; i < countriesOptions.length; i++) {
			if (state.fields.customer_citizenship === countriesOptions[i].value) {
				selectedCitizenshipIndex = i;
				break;
			}
		}

		const signatories = [];

		for (let i = 0; i < state.fields.numberOfSignatories; i++) {
			signatories.push(
				<FieldsCollection
					fieldsPerRow={3}
					key={i}
					fields={[
						{
							render: (
								<EditableField
									name={"customer_signatory_firstname_" + i}
									label="Förnamn"
									value={state.fields["customer_signatory_firstname_" + i]}
									valid={state.fields["customer_signatory_firstname_" + i].length === 0 ? null : true}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									style={{marginBottom: "10px"}}
                                    required={!partner._data.saveDraftButton}
								/>
							),
						},
						{
							render: (
								<EditableField
									name={"customer_signatory_lastname_" + i}
									label="Efternamn"
									value={state.fields["customer_signatory_lastname_" + i]}
									valid={state.fields["customer_signatory_lastname_" + i].length === 0 ? null : true}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									style={{marginBottom: "10px"}}
                                    required={!partner._data.saveDraftButton}
								/>
							),
						},
						{
							render: (
								<EditableField
									type="email"
									name={"customer_signatory_email_" + i}
									label="E-postadress"
									value={state.fields["customer_signatory_email_" + i]}
									valid={state.fields["customer_signatory_email_" + i].length === 0 ? null : Validator.validateEmail(state.fields["customer_signatory_email_" + i])}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
									style={{marginBottom: "10px"}}
                                    required={!partner._data.saveDraftButton}
								/>
							),
						}
					]}
				/>
			);
		}

		let twoYearsAgo = new Date();
		twoYearsAgo = twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<input
					type="hidden"
					name="checkbox_company"
					value={session.isCompany ? "yes" : "no"}
				/>

				<input type="hidden" name="house_comp_name" value={partner.companyName}/>
				<input type="hidden" name="house_address_street" value="Engelbrektsgatan 5"/>
				<input type="hidden" name="house_address_zipcode" value="114 32"/>
				<input type="hidden" name="house_address_city" value="Stockholm"/>
				<input type="hidden" name="house_comp_no" value="5568169626"/>
				<input type="hidden" name="company_name" value={partner.companyName}/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					{!session.lookupData && (
						<FieldsCollection
							fields={[
								{
									render: (
										<Checkbox
											label="Är ett bolag"
											checked={session.isCompany}
											onChange={this._onIsCompanyChange}
										/>
									),
								},
							]}
						/>
					)}

					<FieldsCollection
						fieldsPerRow={session.isCompany ? 2 : 3}
						fields={[
							{
								render: (
									<EditableField
										name="customer_social_security_number"
										label={session.isCompany ? "Organisationsnummer" : "Personnummer"}
										placeholder={session.isCompany ? "XXXXXX-YYYY" : "ÅÅÅÅÅMMDD-XXXX"}
										value={state.fields.customer_social_security_number}
										readOnly={true}
										valid={true}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
                                        required={!partner._data.saveDraftButton}
									/>
								),
							},
							{
								render: (
									<EditableField
										name="customer_firstname"
										label={session.isCompany ? "Bolag" : "Förnamn"}
										value={state.fields.customer_firstname}
										valid={!!session.lookupData || state.validFields.customer_firstname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
                                        required={!partner._data.saveDraftButton}
									/>
								),
							},
							{
								render: (
									<EditableField
										name="customer_lastname"
										label="Efternamn"
										value={state.fields.customer_lastname}
										valid={!!session.lookupData || state.validFields.customer_lastname}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
                                        required={!partner._data.saveDraftButton}
									/>
								),
								display: !session.isCompany,
							}
						]}
					/>

					<input type="hidden" name="customer_name" value={state.fields.customer_firstname + (session.isCompany ? "" : " " + state.fields.customer_lastname)}/>
					<input type="hidden" name="customer_autogiro_custno" value={state.fields.customer_social_security_number}/>
					<input type="hidden" name="customer_social_security_number" value={session.isCompany ? ssnShort : ssnLong}/>

					<FieldsCollection
						fieldsPerRow={2}
						fields={[{
							render: (
								<EditableField
									type="email"
									name="customer_email"
									label={session.isCompany ? "Företagets e-postadress" : "Försäkringstagarens e-postadress"}
									value={state.fields.customer_email}
									valid={state.validFields.customer_email}
									onChange={this._onFieldChange}
									onBlur={this._onFieldBlur}
                  					required={!partner._data.saveDraftButton}
								/>
							)
						},
						{
							render: (
								<Select
									name={"customer_citizenship"}
									label="Medborgarskap"
									options={countriesOptions}
									selectedIndex={selectedCitizenshipIndex}
									onChange={this._onSelectChange}
									style={{maxWidth: "300px"}}
								/>
							),
							display: !session.isCompany
						}
						]}
					/>

					{!session.isCompany && (
						<React.Fragment>
							<input type="hidden" name="futur_insured_ssn" value={state.fields.customer_social_security_number}/>
							<input type="hidden" name="futur_insured_email" value={state.fields.customer_email}/>
							<input type="hidden" name="futur_insured_citizenship" value={state.fields.customer_citizenship}/>
							<input type="hidden" name="checkbox_placement_poa" value={"false"}/>
						</React.Fragment>
					)}

					{session.isCompany && (
						<React.Fragment>
							<h4 style={{margin: "40px 0 0 0"}}>Kontaktuppgifter firmatecknare</h4>

							<p style={{marginTop: "5px"}}>
								Firmatecknare måste signera avtalet, vänligen ange deras kontaktuppgifter.
							</p>

							{signatories}

							<StackLayout style={{width: "60%"}}>
								<Button
									size="small"
									label="Ta bort sista"
									appearance={signatories.length <= 1 ? "medium" : "danger"}
									disabled={signatories.length <= 1}
									onClick={() => CobHelpers.onViewFieldChange.call(this, "numberOfSignatories", state.fields.numberOfSignatories - 1)}
								/>

								<Button
									size="small"
									label="Lägg till firmatecknare"
									appearance={signatories.length >= 5 ? "medium" : "primary"}
									disabled={signatories.length >= 5}
									onClick={() => CobHelpers.onViewFieldChange.call(this, "numberOfSignatories", state.fields.numberOfSignatories + 1)}
									filled
								/>
							</StackLayout>
						</React.Fragment>
					)}

                    {session.isCompany && (
                        <UboFields onChange={this._onUboChange}
                                   session={this.props.session}
                                   draftData={this.props.draftData} />
                    )}

					<h2 style={{marginTop: "40px", marginBottom: "15px"}}>Grunduppgifter</h2>

					<OptionsList
						type="radio"
						orientation="vertical"
						name="kyc_documentation"
						options={state.fields.kyc_documentation.options}
						onChange={this._onOptionsListChange}
					/>

					{state.fields.kyc_documentation.options[0].checked && (
						<React.Fragment>
							<h4 style={{margin: "40px 0 0 0"}}>Datum då grunduppgifter inhämtades</h4>

							<DatePicker selected={state.fields.kyc_date}
										minDate={twoYearsAgo}
										maxDate={new Date()}
										dateFormat="yyyy-MM-dd"
										onSelect={this._handleDateSelect}/>

							<input type={"hidden"} name={"kyc_date"}
								   value={state.fields.kyc_date ? state.fields.kyc_date.toLocaleDateString('sv-SE') : ""}/>
						</React.Fragment>
					)}

					{state.fields.kyc_documentation.options[1].checked && (
						<React.Fragment>

							{session.isCompany && (
								<h4 style={{margin: "40px 0 10px 0"}}>Adress</h4>
							)}

							{!session.isCompany && (
								<h4 style={{margin: "40px 0 10px 0"}}>Adress och kontaktuppgifter</h4>
							)}

							<FieldsCollection
								fieldsPerRow={3}
								fields={[
									{
										render: (
											<EditableField
												name="customer_address_street"
												label="Adress"
												value={state.fields.customer_address_street}
												valid={state.fields.customer_address_street.length > 0}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												className={state.fields.customer_address_street.trim().length === 0 ? "empty" : ""}
                                                required={!partner._data.saveDraftButton}
											/>
										),
									},
									{
										render: (
											<EditableField
												name="customer_address_zipcode"
												label="Postkod"
												value={state.fields.customer_address_zipcode}
												valid={state.fields.customer_address_zipcode.length > 0}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												className={state.fields.customer_address_zipcode.trim().length === 0 ? "empty" : ""}
                                                required={!partner._data.saveDraftButton}
											/>
										),
									}, {
										render: (
                                            <EditableField
                                                name="customer_address_city"
                                                label="Postort"
                                                value={state.fields.customer_address_city}
                                                valid={state.fields.customer_address_city.length > 0}
                                                onChange={this._onFieldChange}
                                                onBlur={this._onFieldBlur}
                                                className={state.fields.customer_address_city.trim().length === 0 ? "empty" : ""}
                                                required={!partner._data.saveDraftButton}
                                            />
										),
									},
								]}
							/>

							{session.isCompany && (
								<h4 style={{margin: "40px 0 10px 0"}}>Kontaktperson</h4>
							)}

							<FieldsCollection
								fieldsPerRow={3}
								fields={[
									{
										render: (
											<EditableField
												name="contact_firstname"
												label="Förnamn"
												value={state.fields.contact_firstname}
												valid={state.fields.contact_firstname.length > 0}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												className={state.fields.contact_firstname.trim().length === 0 ? "empty" : ""}
                                                required={!partner._data.saveDraftButton}
											/>
										),
										display: session.isCompany
									},
									{
										render: (
											<EditableField
												name="contact_lastname"
												label="Efternamn"
												value={state.fields.contact_lastname}
												valid={state.fields.contact_lastname.length > 0}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												className={state.fields.contact_lastname.trim().length === 0 ? "empty" : ""}
                                                required={!partner._data.saveDraftButton}
											/>
										),
										display: session.isCompany
									},
									{
										render: (
											<EditableField
												type="email"
												name="contact_email"
												label="E-postadress"
												value={state.fields.contact_email}
												valid={state.validFields.contact_email}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												className={state.fields.contact_email.trim().length === 0 ? "empty" : ""}
                                                required={!partner._data.saveDraftButton}
											/>
										),
										display: session.isCompany
									},
									{
										render: (
											<EditableField
												type="tel"
												name="customer_phonenumber_home"
												label="Telefon"
												value={state.fields.customer_phonenumber_home}
												valid={state.fields.customer_phonenumber_home.length > 0}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												className={state.fields.customer_phonenumber_home.trim().length === 0 ? "empty" : ""}
                                                required={!partner._data.saveDraftButton}
											/>
										),
									},
									{
										render: (
											<EditableField
												type="tel"
												name="customer_phonenumber_cell"
												label="Mobil"
												value={state.fields.customer_phonenumber_cell}
												onChange={this._onFieldChange}
												onBlur={this._onFieldBlur}
												className={state.fields.customer_phonenumber_cell.trim().length === 0 ? "empty" : ""}
											/>
										),
									}
								]}
							/>

							{!session.isCompany && (
								<input type={"hidden"} name={"contact_email"} value={state.fields.customer_email} />
							)}

							<h4 style={{
								margin: "40px 0 10px 0",
								width: "100%",
								paddingTop: "40px",
								borderTop: "1px solid #DDD"}}>
								Rådgivaren har genomfört identitetskontroll
							</h4>

							<CheckboxWithName
								label="Kopia har tagits på ID-kort"
								name="checkbox_idcheck_id"
								value={state.fields.checkbox_idcheck_id.checked ? "yes" : "no"}
								checked={state.fields.checkbox_idcheck_id.checked}
								onChange={this._onCheckboxChange}
								style={{margin: "5px 0"}}
							/>

							<CheckboxWithName
								label="Kopia har tagits på körkort"
								name="checkbox_idcheck_drivinglic"
								value={state.fields.checkbox_idcheck_drivinglic.checked ? "yes" : "no"}
								checked={state.fields.checkbox_idcheck_drivinglic.checked}
								onChange={this._onCheckboxChange}
								style={{margin: "5px 0"}}
							/>

							<CheckboxWithName
								label="Kopia har tagits på pass"
								name="checkbox_idcheck_passport"
								value={state.fields.checkbox_idcheck_passport.checked ? "yes" : "no"}
								checked={state.fields.checkbox_idcheck_passport.checked}
								onChange={this._onCheckboxChange}
								style={{margin: "5px 0"}}
							/>

							<CheckboxWithName
								label="Kontroll via Bank-ID"
								name="checkbox_idcheck_bankid"
								value={state.fields.checkbox_idcheck_bankid.checked ? "yes" : "no"}
								checked={state.fields.checkbox_idcheck_bankid.checked}
								onChange={this._onCheckboxChange}
								style={{margin: "5px 0"}}
							/>

							<CheckboxWithName
								label="Känd"
								name="checkbox_idcheck_known"
								value={state.fields.checkbox_idcheck_known.checked ? "yes" : "no"}
								checked={state.fields.checkbox_idcheck_known.checked}
								onChange={this._onCheckboxChange}
								style={{margin: "5px 0"}}
							/>

							{(state.fields.checkbox_idcheck_id.checked ||
								state.fields.checkbox_idcheck_drivinglic.checked ||
								state.fields.checkbox_idcheck_passport.checked) && (
								<React.Fragment>
									<p style={{
										width: "100%",
										padding: "20px",
										fontSize: "12px",
										background: "#F1F1F1"
									}}>
										Ladda upp kopia på identitetshandling mha uppladdningsrutan längre ner i ansökan.
									</p>
								</React.Fragment>
							)}

							{session.isCompany && (
								<React.Fragment>
									<h4 style={{margin: "40px 0 10px 0"}}>
										Kontroll av verklig huvudman (i förekommande fall)
									</h4>

									<CheckboxWithName
										label="ID-kort"
										name="ubo_checkbox_idcheck_id"
										value={state.fields.ubo_checkbox_idcheck_id.checked ? "yes" : "no"}
										checked={state.fields.ubo_checkbox_idcheck_id.checked}
										onChange={this._onCheckboxChange}
										style={{margin: "5px 0"}}
									/>

									<CheckboxWithName
										label="Körkort"
										name="ubo_checkbox_idcheck_drivinglic"
										value={state.fields.ubo_checkbox_idcheck_drivinglic.checked ? "yes" : "no"}
										checked={state.fields.ubo_checkbox_idcheck_drivinglic.checked}
										onChange={this._onCheckboxChange}
										style={{margin: "5px 0"}}
									/>

									<CheckboxWithName
										label="Pass"
										name="ubo_checkbox_idcheck_passport"
										value={state.fields.ubo_checkbox_idcheck_passport.checked ? "yes" : "no"}
										checked={state.fields.ubo_checkbox_idcheck_passport.checked}
										onChange={this._onCheckboxChange}
										style={{margin: "5px 0"}}
									/>

									<CheckboxWithName
										label="Bank-ID"
										name="ubo_checkbox_idcheck_bankid"
										value={state.fields.ubo_checkbox_idcheck_bankid.checked ? "yes" : "no"}
										checked={state.fields.ubo_checkbox_idcheck_bankid.checked}
										onChange={this._onCheckboxChange}
										style={{margin: "5px 0"}}
									/>

									{(state.fields.ubo_checkbox_idcheck_id.checked ||
										state.fields.ubo_checkbox_idcheck_drivinglic.checked ||
										state.fields.ubo_checkbox_idcheck_passport.checked) && (
										<React.Fragment>
											<p style={{
												width: "100%",
												padding: "20px",
												fontSize: "12px",
												background: "#F1F1F1"
											}}>
												Ladda upp kopia på identitetshandling mha uppladdningsrutan längre ner i ansökan.
											</p>
										</React.Fragment>
									)}
								</React.Fragment>
							)}

							<h4 style={{
								margin: "40px 0 10px 0",
								width: "100%",
								paddingTop: "40px",
								borderTop: "1px solid #DDD"}}>
								Kunden är klassificerad som icke-professionell
							</h4>

							<OptionsList
								type="radio"
								orientation="horizontal"
								name="checkbox_customer_kyc_non_proff"
								options={state.fields.checkbox_customer_kyc_non_proff.options}
								onChange={this._onOptionsListChange}
							/>

							<h4 style={{margin: "40px 0 10px 0"}}>
								Rådgivning på distans (i förekommande fall, samtliga ska uppfyllas)
							</h4>

							<OptionsList
								type="radio"
								orientation="horizontal"
								name="radio_distans"
								options={state.fields.radio_distans.options}
								onChange={this._onOptionsListChange}
							/>

							{state.fields.radio_distans.options[1].checked && (
								<React.Fragment>
									<input type={"hidden"} name={"checkbox_customer_extcheck"} value={"no"}/>
									<input type={"hidden"} name={"checkbox_customer_countercheck"} value={"no"}/>
								</React.Fragment>
							)}

							{state.fields.radio_distans.options[1].checked && (
								<React.Fragment>
									<CheckboxWithName
										label="Kontroll mot externa register (exempelvis Eniro, som bilaga)"
										name="checkbox_customer_extcheck"
										value={state.fields.checkbox_customer_extcheck.checked ? "yes" : "no"}
										checked={state.fields.checkbox_customer_extcheck.checked}
										onChange={this._onCheckboxChange}
										style={{margin: "20px 0 5px 0"}}
									/>

									<CheckboxWithName
										label="Utfört motkontroll"
										name="checkbox_customer_countercheck"
										value={state.fields.checkbox_customer_countercheck.checked ? "yes" : "no"}
										checked={state.fields.checkbox_customer_countercheck.checked}
										onChange={this._onCheckboxChange}
										style={{margin: "5px 0"}}
									/>
								</React.Fragment>
							)}
						</React.Fragment>
					)}
				</div>
			</div>
		);
	}

	// Internal methods
	_fieldValidator = (name, value) => {
		if (name === "customer_social_security_number") {
			return this.props.session.isCompany ? Validator.validateOrgNum(value, true) : Validator.validateSsn(value, true);
		}
		else if (name === "customer_email" || name === "contact_email") {
			return Validator.validateEmail(value);
		}
		else {
			return value.length > 0;
		}
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};

	_onSelectChange = (option, index) => {
		CobHelpers.onViewFieldChange.call(this, "customer_citizenship", option.value);
	};

	_handleDateSelect = (value) => {
		CobHelpers.onViewFieldChange.call(this, "kyc_date", value);
	};

	_onUboChange = (valid) => {
		CobHelpers.onViewFieldChange.call(this, "ubo_fields_valid", valid);
	};

	_onCheckboxChange = (name, checked) => {
		this.setState({
			fields: {
				...this.state.fields,
				[name]: {...this.state.fields[name], checked}
			}
		}, () => {
			const documentsRequired = [];

			if (this.state.fields.checkbox_idcheck_passport.checked) {
				documentsRequired.push("Kopia på pass");
			}

			if (this.state.fields.checkbox_idcheck_drivinglic.checked) {
				documentsRequired.push("Kopia på körkort");
			}

			if (this.state.fields.checkbox_idcheck_id.checked) {
				documentsRequired.push("Kopia på ID-kort");
			}

			if (this.state.fields.ubo_checkbox_idcheck_id.checked) {
				documentsRequired.push("Kopia på ID-kort (verklig huvudman)");
			}

			if (this.state.fields.ubo_checkbox_idcheck_drivinglic.checked) {
				documentsRequired.push("Kopia på körkort (verklig huvudman)");
			}

			if (this.state.fields.ubo_checkbox_idcheck_passport.checked) {
				documentsRequired.push("Kopia på pass (verklig huvudman)");
			}

			this.props.actions.setAppState({
				documentsRequired: documentsRequired
			});
		});
	};

	_onIsCompanyChange = (checked) => {
		const {session, onChange} = this.props;

		session.isCompany = checked;
		if (this.props.session.isCompany !== checked) this.setState(_getInitialState.call(this), () => {
			onChange({...this.state}, false, this.props.number);
		});
	};

	_onOptionsListChange = ({type, name}, option, index) => {
		const newOptions = [...this.state.fields[name].options].map((o, i) => {
			if (i === index) {
				return option;
			}
			else {
				return {...o, checked: type === "radio" ? false : o.checked};
			}
		});

		this.setState({
			fields: {
				...this.state.fields,
				[name]: {
					options: newOptions,
				},
			},
		}, () => {
			fulfillmentCheck(this.props, this.state);

			const documentsRequired = [];

			if (this.state.fields.kyc_documentation.options[1].checked) {
				if (this.state.fields.checkbox_idcheck_passport.checked) {
					documentsRequired.push("Kopia på pass");
				}

				if (this.state.fields.checkbox_idcheck_drivinglic.checked) {
					documentsRequired.push("Kopia på körkort");
				}

				if (this.state.fields.checkbox_idcheck_id.checked) {
					documentsRequired.push("Kopia på ID-kort");
				}

				if (this.state.fields.ubo_checkbox_idcheck_id.checked) {
					documentsRequired.push("Kopia på ID-kort (verklig huvudman)");
				}

				if (this.state.fields.ubo_checkbox_idcheck_drivinglic.checked) {
					documentsRequired.push("Kopia på körkort (verklig huvudman)");
				}

				if (this.state.fields.ubo_checkbox_idcheck_passport.checked) {
					documentsRequired.push("Kopia på pass (verklig huvudman)");
				}
			}

			this.props.actions.setAppState({
				documentsRequired: documentsRequired
			});
		});
	};
}


// PRIVATE FUNCTIONS
function _getFields() {
	return [
		"customer_firstname",
		"customer_lastname",
		"customer_social_security_number",
		"customer_email",
		"customer_citizenship",
		"customer_signatory_email_0",
		"customer_signatory_email_1",
		"customer_signatory_email_2",
		"customer_signatory_email_3",
		"customer_signatory_email_4",
		"kyc_date",
		"checkbox_customer_extcheck",
		"checkbox_customer_countercheck",

		"contact_firstname",
		"contact_lastname",
		"contact_email",
		"customer_phonenumber_home",
		"customer_phonenumber_cell",
	];
}

function _getInitialState() {
	if (this.props.prevState) return {...this.props.prevState};

	const {session} = this.props;
	const {isCompany} = session;
	const getLookupData = (name) => session.lookupData ? session.lookupData[name] || "" : "";

	return {
		busy: false,
		company: isCompany,
		validFields: !session.lookupData ? {} : {
			"customer_firstname": !!getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_social_security_number": !!getLookupData(isCompany ? "orgNumber" : "ssn"),
			...(
				!isCompany ? {
					"customer_lastname": !!getLookupData("lastName")
				} : undefined
			),
		},
		fields: {
			"numberOfSignatories": 1,
			"customer_firstname": getLookupData(isCompany ? "companyName" : "firstName"),
			"customer_lastname": !isCompany ? getLookupData("lastName") : "",
			"customer_social_security_number": getLookupData(isCompany ? "orgNumber" : "ssn"),
			"customer_email": "",
			"customer_citizenship": "SE",
			"customer_signatory_firstname_0": "",
			"customer_signatory_firstname_1": "",
			"customer_signatory_firstname_2": "",
			"customer_signatory_firstname_3": "",
			"customer_signatory_firstname_4": "",
			"customer_signatory_lastname_0": "",
			"customer_signatory_lastname_1": "",
			"customer_signatory_lastname_2": "",
			"customer_signatory_lastname_3": "",
			"customer_signatory_lastname_4": "",
			"customer_signatory_email_0": "",
			"customer_signatory_email_1": "",
			"customer_signatory_email_2": "",
			"customer_signatory_email_3": "",
			"customer_signatory_email_4": "",
			"kyc_date": "",

			"customer_address_street": getLookupData("street"),
			"customer_address_zipcode": getLookupData("zipCode"),
			"customer_address_city": getLookupData("city"),

			"contact_firstname": "",
			"contact_lastname": "",
			"contact_email": "",
			"customer_phonenumber_home": "",
			"customer_phonenumber_cell": "",

			"kyc_documentation": {
				options: [
					{
						label: "Kunden är känd, de senaste grunduppgifterna upprättades inom 24 månader från tillfället för denna rådgivning och inga betydande förändringar har skett",
						value: "not_included",
						checked: false
					},
					{
						label: "Nya grunduppgifter om kunden bifogas",
						value: "included",
						checked: false
					}
				]
			},

			ubo_fields_valid: false,

			checkbox_idcheck_id: {
				checked: false
			},

			checkbox_idcheck_drivinglic: {
				checked: false
			},

			checkbox_idcheck_passport: {
				checked: false
			},

			checkbox_idcheck_bankid: {
				checked: false
			},

			checkbox_idcheck_known: {
				checked: false
			},

			ubo_checkbox_idcheck_id: {
				checked: false
			},

			ubo_checkbox_idcheck_drivinglic: {
				checked: false
			},

			ubo_checkbox_idcheck_passport: {
				checked: false
			},

			ubo_checkbox_idcheck_bankid: {
				checked: false
			},

			checkbox_customer_extcheck: {
				checked: false
			},

			checkbox_customer_countercheck: {
				checked: false
			},

			checkbox_customer_kyc_non_proff: {
				options: [
					{
						label: "Nej",
						value: "no",
						checked: false
					},
					{
						label: "Ja",
						value: "yes",
						checked: false
					}
				]
			},

			radio_distans: {
				options: [
					{
						label: "Nej",
						value: "no",
						checked: false
					},
					{
						label: "Ja",
						value: "yes",
						checked: false
					}
				]
			}
		},
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/CustomerInformation/fair-futur/CustomerInformation.js